import { type JSX, Show, splitProps } from "solid-js";
import { cn } from "@malla/shared";

/**
 * Inline SVG illustrations for empty states, with gastronomic identity.
 *
 * All are theme-aware: using `currentColor` (container tone) and `fill`
 * with low opacity for filling, so they inherit theme color
 * without hardcoded tones. Decorative → `aria-hidden`.
 */
type Illustration = "tables" | "menu" | "orders" | "plate" | "reservation";

const strokeProps = {
  fill: "none",
  stroke: "currentColor",
  "stroke-width": "1.5",
  "stroke-linecap": "round" as const,
  "stroke-linejoin": "round" as const,
};

/**
 * Map of illustrations → group of SVG paths. Data-driven (no early returns)
 * to maintain Solid reactivity: `<svg>` is rendered once and
 * content is reactively selected by `name`.
 */
const SHAPES: Record<Illustration, () => JSX.Element> = {
  tables: () => (
    <>
      <circle cx="48" cy="40" r="20" fill="currentColor" opacity="0.08" />
      <circle cx="48" cy="40" r="20" {...strokeProps} />
      <path d="M48 60v16" {...strokeProps} />
      <path d="M36 84h24" {...strokeProps} />
      <circle cx="24" cy="34" r="5" {...strokeProps} />
      <circle cx="72" cy="34" r="5" {...strokeProps} />
    </>
  ),
  menu: () => (
    <>
      <rect x="26" y="18" width="44" height="60" rx="5" fill="currentColor" opacity="0.08" />
      <rect x="26" y="18" width="44" height="60" rx="5" {...strokeProps} />
      <path d="M36 34h24M36 44h24M36 54h16" {...strokeProps} />
    </>
  ),
  orders: () => (
    <>
      <path
        d="M30 22h36l-3 50a4 4 0 0 1-4 4H37a4 4 0 0 1-4-4z"
        fill="currentColor"
        opacity="0.08"
      />
      <path d="M30 22h36l-3 50a4 4 0 0 1-4 4H37a4 4 0 0 1-4-4z" {...strokeProps} />
      <path d="M24 22h48" {...strokeProps} />
      <path d="M40 16h16v6H40z" {...strokeProps} />
      <path d="M42 40v22M54 40v22" {...strokeProps} />
    </>
  ),
  reservation: () => (
    <>
      <rect x="22" y="26" width="52" height="48" rx="6" fill="currentColor" opacity="0.08" />
      <rect x="22" y="26" width="52" height="48" rx="6" {...strokeProps} />
      <path d="M22 40h52M34 20v12M62 20v12" {...strokeProps} />
      <path d="M40 56l5 5 11-11" {...strokeProps} />
    </>
  ),
  plate: () => (
    <>
      <circle cx="48" cy="48" r="26" fill="currentColor" opacity="0.08" />
      <circle cx="48" cy="48" r="26" {...strokeProps} />
      <circle cx="48" cy="48" r="14" {...strokeProps} />
    </>
  ),
};

function Illustrations(props: { name: Illustration; class?: string }) {
  return (
    <svg viewBox="0 0 96 96" class={props.class} aria-hidden="true" role="presentation">
      {SHAPES[props.name]()}
    </svg>
  );
}

interface EmptyStateProps {
  /** Gastronomic illustration to display. */
  illustration?: Illustration;
  /** Short title. */
  title: JSX.Element;
  /** Optional description. */
  description?: JSX.Element;
  /** Optional action (button/CTA). */
  action?: JSX.Element;
  /**
   * When provided, the illustration becomes an interactive target that
   * triggers the same action as the primary CTA (opening a dialog, navigating, or
   * focusing an input), so the empty state is never a dead end.
   * Tap/click/keyboard (Enter/Space, native to `<button>`) activate it.
   * Leave undefined in purely informational empty states (illustration
   * remains decorative).
   */
  onIconActivate?: () => void;
  /**
   * `aria-label` of the illustration button (recommended when there is
   * `onIconActivate`). Must describe the action, e.g. "Add your first table".
   * If omitted, falls back to `title` when it is text.
   */
  iconActionLabel?: string;
  class?: string;
  "data-testid"?: string;
}

/**
 * Consistent empty state: themed SVG illustration + text + optional action.
 * Smooth entrance (fade-in) that respects `prefers-reduced-motion` via the
 * global guard in global.css.
 *
 * When `onIconActivate` is passed, the illustration becomes an accessible
 * `<button>` (visible focus, subtle hover, `active:scale` only under motion-safe)
 * that triggers the primary CTA; in informational empty states it remains decorative.
 */
export function EmptyState(props: EmptyStateProps) {
  const [local] = splitProps(props, [
    "illustration",
    "title",
    "description",
    "action",
    "onIconActivate",
    "iconActionLabel",
    "class",
    "data-testid",
  ]);

  return (
    <div
      data-testid={local["data-testid"]}
      class={cn(
        "flex animate-fade-in flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/60 bg-surface/40 px-6 py-16 text-center",
        local.class,
      )}
    >
      <Show
        when={local.onIconActivate}
        fallback={
          <div class="text-accent/70">
            <Illustrations name={local.illustration ?? "plate"} class="h-20 w-20" />
          </div>
        }
      >
        {(activate) => (
          <button
            type="button"
            aria-label={
              local.iconActionLabel ?? (typeof local.title === "string" ? local.title : undefined)
            }
            onClick={() => {
              activate()();
            }}
            class="group rounded-2xl p-1 text-accent/70 outline-none transition-[transform,color] duration-200 hover:text-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-95 motion-safe:hover:scale-105"
          >
            <Illustrations name={local.illustration ?? "plate"} class="h-20 w-20" />
          </button>
        )}
      </Show>
      <p class="text-base font-semibold text-foreground">{local.title}</p>
      <Show when={local.description}>
        <p class="max-w-xs text-sm text-muted-foreground">{local.description}</p>
      </Show>
      <Show when={local.action}>
        <div class="mt-1">{local.action}</div>
      </Show>
    </div>
  );
}
